/* Los datos del hub adentro de FiboSheets: las fórmulas HUB.…
 *
 * ── QUÉ ES ─────────────────────────────────────────────────────────────────
 *
 * Igna, 04/10/2026: «fórmulas en una planilla que traigan datos vivos del
 * hub». =HUB.PRECIO_GASOIL() da el litro del D-500 que declaró Carlos Becerra;
 * =HUB.VIAJES("01/10/2026";"31/10/2026";"sur") cuenta los viajes al sur del
 * mes. El motor (`planilla.js`) no sabe nada de esto: le llega `opciones.hub`
 * y le pregunta. Este archivo es ese `hub`.
 *
 * ── UNA LISTA CERRADA, NO UNA PUERTA A LA BASE ──────────────────────────────
 *
 * Nada de «HUB.TABLA("personas"; "telefono")». Cada función es una pregunta
 * conocida, con sus argumentos validados, sobre una fuente que ya usa alguna
 * pantalla del hub: si una fórmula pudiera nombrar tablas y columnas, la
 * planilla sería una consola SQL con otra cara. Para sumar una función se
 * agrega acá, con su fuente, y se prueba.
 *
 * ── QUIÉN DECIDE QUÉ SE VE: LA RLS, CON LA SESIÓN DE QUIEN MIRA ─────────────
 *
 * Los datos se leen con la sesión de quien tiene la planilla abierta (ver
 * `services/hubDatos.js`), así que la base contesta lo que esa persona ya
 * puede ver: una planilla compartida no le muestra a nadie más de lo que
 * vería en su propia pantalla. Encima de eso, cada función pide el permiso de
 * la pantalla de donde sale su dato (`ver.ypf`, `ver.presidencia`…). No es la
 * pared —la pared es la base— sino el cartel: sin permiso la RLS devuelve
 * cero filas, y «0 viajes» dicho a quien no puede ver viajes es una mentira
 * con forma de número. Mejor un #HUB que diga que no hay acceso — sin nombrar
 * la llave, como el resto del hub.
 *
 * HUB.UBICACION es la excepción de fuente, no de regla: la posición vive en
 * FlotaBot y no en la base, así que se lee con la sesión de quien mira EN
 * FlotaBot (el puente SSO de siempre), y FlotaBot pide la misma llave.
 *
 * En el servidor (la edge function que usa el bot) no hay sesión y no hay
 * hub: ahí toda HUB.… vale #HUB. Leer con service_role sería saltear la RLS
 * en nombre del dueño.
 *
 * ── ASÍNCRONO ADENTRO DE UN MOTOR SÍNCRONO ─────────────────────────────────
 *
 * El motor calcula de una pasada y no espera a nadie. Así que `leer` contesta
 * en el acto con lo que hay en el caché: el dato, «cargando» (y sale a
 * buscarlo), o el error. Cuando el dato llega, `alLlegar()` avisa y la
 * pantalla vuelve a calcular la hoja; esta vez está en el caché.
 *
 * EL CACHÉ ES POR FUENTE, NO POR CELDA. Una columna con =HUB.VEHICULO(A2;
 * "patente") para cincuenta internos trae el padrón de la flota UNA vez, y
 * cada celda busca el suyo adentro. Dos celdas que preguntan lo mismo
 * comparten el pedido que está en viaje. Y como se recalcula en cada tecla,
 * eso es lo que hace que tipear no dispare un pedido por tecla.
 *
 * Vive TTL_MS. Pasado eso el dato viejo se sigue mostrando mientras se trae
 * el nuevo (no parpadea a «…» cada minuto), y la pantalla le da un empujón
 * cada TTL_MS para que una planilla abierta se actualice sola. Un error se
 * recuerda menos (TTL_ERROR_MS): se reintenta pronto, pero no en cada tecla.
 *
 * LO QUE NO PUEDE ENVEJECER CALLADO. Mostrar lo viejo mientras se reintenta
 * está bien para el gasoil; para dónde está un camión, no: con FlotaBot caído
 * una hora, «Mercado Central» seguiría ahí como si fuera de ahora. Una fuente
 * con `vence` (la ubicación: VENCE_UBICACION_MS) deja de mostrarse pasado ese
 * tiempo desde la última lectura buena: si lo último que pasó fue un error,
 * #HUB con desde cuándo y por qué; si sólo nadie preguntó en un rato, «…»
 * hasta que llega la nueva. La primera lectura buena lo vuelve a mostrar.
 */

import { fechaASerial, serialAFecha } from "./planilla";
import { precioDeclarado } from "./ypf";
import { estadoDeUnidad, textoDeEstado, textoDeEstadoTractor } from "../services/cols/estado";
import { etiquetaTipo as etiquetaTipoAcoplado } from "./tipoAcoplado";
import { claveDni, claveInterno } from "./asignacion";
import { nombreDePersona } from "./personas";
import { ETIQUETA as ETIQUETA_RTO } from "./rto";
import { comoParte } from "./crt";
import { hojasDe } from "./mic";
import { diaAr, enAR } from "./fechaAr";

export const TTL_MS = 60 * 1000;
export const TTL_ERROR_MS = 15 * 1000;
/* Diez minutos sin poder actualizar la ubicación (unos cuarenta reintentos)
   y deja de mostrarse: un corte de un pedido no la apaga, uno de verdad sí. */
export const VENCE_UBICACION_MS = 10 * 60 * 1000;

/* Un error con el código que va a mostrar la celda y el porqué para el mouse. */
export class ErrorHub extends Error {
  constructor(codigo, motivo) {
    super(motivo);
    this.codigo = codigo;
    this.motivo = motivo;
  }
}
const mal = (motivo) => new ErrorHub("#VALOR", motivo);
const noHay = (motivo) => new ErrorHub("#N/D", motivo);

/* ── Leer los argumentos ─────────────────────────────────────────────────── */

const vacio = (v) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");
const dd = (n) => String(n).padStart(2, "0");

/* Una fecha como la escribe una celda —DD/MM/AAAA, o el serial que da
   =HOY()+1 o =FECHA(…)— o como AAAA-MM-DD → "AAAA-MM-DD". */
export function fechaISO(v, cual) {
  let ddmm = null;
  if (typeof v === "number" && Number.isFinite(v)) ddmm = serialAFecha(v);
  else if (typeof v === "string") {
    const s = v.trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (iso) ddmm = `${iso[3]}/${iso[2]}/${iso[1]}`;
    else if (fechaASerial(s) !== null) ddmm = s;
  }
  if (ddmm === null || fechaASerial(ddmm) === null) {
    throw mal(`${cual} tiene que ser una fecha (DD/MM/AAAA); llegó «${vacio(v) ? "nada" : v}».`);
  }
  const [d, m, a] = ddmm.split("/");
  return `${a}-${dd(m)}-${dd(d)}`;
}

/* Desde y hasta, en orden y sin pasarse de `topeDias`. */
function rangoDeFechas(desde, hasta, topeDias) {
  const a = fechaISO(desde, "«desde»");
  const b = fechaISO(hasta, "«hasta»");
  if (a > b) throw mal(`«desde» (${a}) es posterior a «hasta» (${b}).`);
  const dias = (Date.parse(b) - Date.parse(a)) / 86400000 + 1;
  if (topeDias && dias > topeDias) throw mal(`Son ${dias} días; el tope es ${topeDias}. Partilo en tramos.`);
  return { desde: a, hasta: b };
}

const texto = (v) => (vacio(v) ? "" : String(v).trim());

/* Un interno como lo escribe la gente: 774, "774", "F774", "f 774". Con la F
   es un furgón, pelado es un tractor: la convención de `shared/unidad.js` y
   del taller. El número sin ceros adelante, como `claveInterno`. */
export function unidadDe(v) {
  const s = texto(typeof v === "number" ? String(Math.trunc(v)) : v).toUpperCase();
  const n = claveInterno(s);
  if (!n) return null;
  const tipo = s.startsWith("F") ? "furgon" : "tractor";
  return { tipo, numero: n, clave: tipo === "furgon" ? `F${n}` : n };
}

/* El campo pedido, de una lista cerrada. Sin campo, el primero. */
function campoDe(v, campos, funcion) {
  const c = texto(v).toLowerCase();
  if (!c) return campos[0];
  if (!campos.includes(c)) throw mal(`HUB.${funcion} no tiene el campo «${c}». Los que hay: ${campos.join(", ")}.`);
  return c;
}

function cantidad(args, min, max, funcion, firma) {
  if (args.length < min || args.length > max) {
    throw mal(`HUB.${funcion} lleva ${min === max ? min : `de ${min} a ${max}`} argumento${max === 1 ? "" : "s"}: ${firma}.`);
  }
}

/* Para comparar nombres: sin tildes, sin mayúsculas, sin comas ni dobles
   espacios. «Pérez, Juan» y «juan perez» son la misma persona. */
export function normalizarNombre(s) {
  return String(s ?? "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/* ── Las fuentes: de dónde sale cada dato ──────────────────────────────────
 *
 * Cada fuente es UNA lectura (`traer`, que recibe la capa de datos) con una
 * `clave` para el caché. Las que no dependen de los argumentos (el padrón de
 * la flota, los precios declarados) son una sola para toda la planilla. */

const FUENTE = {
  permisos: { clave: "permisos", que: "tus permisos", traer: (d) => d.permisos() },
  gasoil: { clave: "gasoil", que: "el precio declarado del gasoil", traer: (d) => d.preciosGasoil() },
  flota: { clave: "flota", que: "el padrón de la flota", traer: (d) => d.flota() },
  personas: { clave: "personas", que: "el padrón de personal", traer: (d) => d.personas() },
  asignaciones: { clave: "asignaciones", que: "qué tractor tiene cada chofer", traer: (d) => d.asignaciones() },
  viajes: (p) => ({
    clave: `viajes|${p.desde}|${p.hasta}|${p.sentido}|${p.cliente.toLowerCase()}`,
    que: "los viajes",
    traer: (d) => d.viajes(p),
  }),
  urea: (p) => ({ clave: `urea|${p.desde}|${p.hasta}`, que: "las cargas de urea", traer: (d) => d.urea(p) }),
  /* Las dos listas del RTO van por separado: una planilla de tractores no
     tiene por qué esperar los ~680 furgones (cada fila es un `rto_estado`). */
  rto: (tipo) => ({
    clave: `rto|${tipo}`,
    que: tipo === "furgon" ? "el RTO de los furgones" : "el RTO de los tractores",
    traer: (d) => d.rto(tipo),
  }),
  multas: { clave: "multas", que: "las multas de tránsito", traer: (d) => d.multas() },
  documentos: { clave: "documentos", que: "la documentación de los choferes", traer: (d) => d.documentos() },
  mic: { clave: "mic", que: "los MIC", traer: (d) => d.mic() },
  crt: { clave: "crt", que: "los CRT", traer: (d) => d.crt() },
  stock: { clave: "stock", que: "el stock del pañol", traer: (d) => d.stock() },
  depositos: { clave: "depositos", que: "los depósitos del pañol", traer: (d) => d.depositos() },
  /* Toda la flota con satelital de una vez (FlotaBot, GET /api/ubicaciones):
     una columna de 200 unidades es UN pedido por recálculo, no 200. Con
     `vence`: una ubicación vieja no se muestra como si fuera de ahora (ver
     «Lo que no puede envejecer callado», arriba). */
  ubicaciones: {
    clave: "ubicaciones", que: "la ubicación satelital", traer: (d) => d.ubicaciones(), vence: VENCE_UBICACION_MS,
  },
};

/* ── Las funciones ─────────────────────────────────────────────────────────
 *
 * Cada una: su firma y su ayuda (lo que muestra la pantalla), qué permiso
 * pide (alcanza con uno de la lista), cómo lee sus argumentos (`preparar`, que
 * tira ErrorHub si no sirven), qué fuentes necesita y cómo calcula con ellas.
 * `calcular` es puro: recibe los datos ya traídos, en el orden de `fuentes`. */

const CAMPOS_VEHICULO = ["patente", "tipo", "estado", "marca", "modelo", "chofer"];
const CAMPOS_CHOFER = ["nombre", "legajo", "dni", "puesto", "estado", "tractor"];
const CAMPOS_RTO = ["vence", "estado", "dias"];
const CAMPOS_MULTAS = ["cantidad", "monto", "impagas", "monto_impago"];
const CAMPOS_MIC = ["estado", "fecha", "crt", "hoja", "placa", "acoplado", "chofer", "peso", "bultos", "partida", "destino"];
const CAMPOS_CRT = ["estado", "emitido", "carga", "origen", "entrega", "remitente", "destinatario", "mercaderia", "peso", "bultos", "valor", "moneda", "camiones"];

/* El chofer que tiene asignado un tractor, por nombre: «PÉREZ, JUAN». Si son
   varios (pasa: 178 de 307 tractores llevaron más de uno), van todos. Se
   exporta para la tabla de la flota (`shared/tablasHub`): la misma respuesta
   que HUB.VEHICULO(…;"chofer"), no una segunda versión. */
export function choferesDe(numero, asignaciones, personas) {
  const porDni = new Map((personas || []).map((p) => [claveDni(p.dni), p]));
  return (asignaciones || [])
    .filter((a) => claveInterno(a.interno) === numero)
    .map((a) => {
      const p = porDni.get(claveDni(a.dni));
      return (p && nombreDePersona(p)) || `DNI ${a.dni}`;
    })
    .join(" / ");
}

function buscarUnidad(flota, u) {
  const filas = (u.tipo === "furgon" ? flota?.furgones : flota?.tractores) || [];
  // `id` es el interno en los dos padrones; `interno` también, por las dudas.
  return filas.find((f) => claveInterno(f.id) === u.numero)
    || filas.find((f) => claveInterno(f.interno) === u.numero)
    || null;
}

/* La persona por legajo, DNI o nombre. Si coinciden varias, gana la única
   activa; si no hay una sola, se dice quiénes son en vez de elegir una:
   adivinar es contestar sobre la persona equivocada con total confianza. */
export function buscarPersona(personas, quien) {
  const filas = personas || [];
  const digitos = String(quien).replace(/[\s.]/g, "");
  let candidatas;
  if (/^\d+$/.test(digitos)) {
    const n = digitos.replace(/^0+/, "");
    candidatas = filas.filter((p) => claveDni(p.dni) === n || claveInterno(p.legajo) === n);
  } else {
    const q = normalizarNombre(quien);
    const palabras = q.split(" ").filter(Boolean);
    const nombres = (p) => [
      normalizarNombre([p.apellido, p.nombres].filter(Boolean).join(" ")),
      normalizarNombre([p.nombres, p.apellido].filter(Boolean).join(" ")),
      normalizarNombre(p.nombre),
    ];
    candidatas = filas.filter((p) => nombres(p).includes(q));
    if (!candidatas.length && palabras.length) {
      candidatas = filas.filter((p) => {
        const todas = new Set(nombres(p).join(" ").split(" "));
        return palabras.every((w) => todas.has(w));
      });
    }
  }
  if (candidatas.length > 1) {
    const activas = candidatas.filter((p) => estadoDeUnidad(p.estado) === "activo");
    if (activas.length === 1) return activas[0];
    const quienes = (activas.length ? activas : candidatas).slice(0, 3).map((p) => nombreDePersona(p)).join("; ");
    throw noHay(`Coinciden ${activas.length || candidatas.length} personas (${quienes}…). Usá el legajo o el DNI.`);
  }
  if (!candidatas.length) throw noHay(`No encontré a «${quien}» en el padrón de personal.`);
  return candidatas[0];
}

/* ── Patentes, números de papel, artículos y depósitos ─────────────────────
 *
 * Las funciones que se sumaron el 05/10/2026 (RTO, multas, documentos,
 * aduana y pañol) buscan por cosas que la gente escribe de muchas formas.
 * Cada búsqueda vive acá, probada, y con la misma regla que `buscarPersona`:
 * si no hay UNA sola respuesta, se dice cuáles coinciden en vez de elegir. */

/* Una patente como la guarda la base (`normalizar_dominio`): mayúsculas y sin
   espacios ni guiones. «ah 989 gd» y «AH-989-GD» son la misma chapa. */
export const normalizarPatente = (v) => String(v ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/* Las tres formas de patente que acepta `patente_valida` en la base. */
const PATENTE = /^([A-Z]{3}\d{3}|[A-Z]{2}\d{3}[A-Z]{2}|[A-Z]{3}\d{4})$/;

/* Interno, patente o DNI: qué escribió. Un DNI son 7 a 9 dígitos; un interno
   nunca pasa de cuatro, así que no se pisan. */
export function vehiculoDe(v) {
  const s = normalizarPatente(typeof v === "number" ? String(Math.trunc(v)) : v);
  if (PATENTE.test(s)) return { patente: s };
  if (/^\d{7,9}$/.test(s)) return { dni: s };
  const u = unidadDe(v);
  return u ? { unidad: u } : null;
}

const nombreUnidad = (u) => (u.tipo === "furgon" ? `el furgón ${u.numero}` : `el tractor ${u.numero}`);
const mayuscula = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/* Un número de MIC o CRT: entero («AR.142.067525», con o sin espacios) o sólo
   el correlativo («67525»), que es como se lo dicen por teléfono. Con el
   correlativo solo puede haber dos de series distintas: se dicen los dos.

   UN NÚMERO PUEDE ESTAR DOS VECES (Fabián, 08/10). Anular un CRT deja su
   número libre para el siguiente, y el anulado lo conserva. Las filas vienen
   ordenadas por id, así que un `find` a secas devolvía el ANULADO —el más
   viejo— y la fórmula contestaba sobre un embarque que se canceló.

   Y desde el 16/10 un borrador no reserva su número (ticket #427,
   `crt_numero_unico` mira sólo los emitidos): un borrador puede tener el
   mismo que un CRT EMITIDO, y como suele ser más nuevo, «el último vivo» era
   el borrador. Así que entre los del mismo número gana el emitido, que es el
   papel que salió; si no hay, el vivo (el último borrador); si están todos
   anulados, el último. */
const elDeEseNumero = (iguales) => {
  const entre = [
    iguales.filter((f) => f.estado === "emitido"),
    iguales.filter((f) => f.estado !== "anulado"),
    iguales,
  ].find((g) => g.length);
  return entre[entre.length - 1];
};

export function buscarNumero(filas, numero, que) {
  const limpio = (x) => String(x ?? "").toUpperCase().replace(/\s+/g, "");
  const q = limpio(typeof numero === "number" ? String(Math.trunc(numero)) : numero);
  if (!q) throw mal(`Falta el número del ${que}.`);
  const exactos = (filas || []).filter((f) => limpio(f.numero) === q);
  if (exactos.length) return elDeEseNumero(exactos);
  if (/^\d+$/.test(q)) {
    const n = Number(q);
    const porNumero = new Map();
    for (const f of filas || []) {
      const t = limpio(f.numero);
      const ult = t.slice(t.lastIndexOf(".") + 1);
      if (!/^\d+$/.test(ult) || Number(ult) !== n) continue;
      if (!porNumero.has(t)) porNumero.set(t, []);
      porNumero.get(t).push(f);
    }
    const cand = [...porNumero.values()].map(elDeEseNumero);
    if (cand.length === 1) return cand[0];
    if (cand.length > 1) {
      throw noHay(`Hay ${cand.length} ${que} que terminan en ${q} (${cand.slice(0, 3).map((f) => f.numero).join("; ")}). Escribí el número entero.`);
    }
  }
  throw noHay(`No encontré el ${que} «${texto(numero)}».`);
}

/* Un artículo del pañol por código (el `id`, «AC070012») o por descripción.
   La descripción vale entera o por palabras sueltas («manguera radiador»);
   si coinciden varios, se nombran con su código para que se use ése. */
export function buscarArticulo(stock, quien) {
  const filas = stock || [];
  const cod = texto(quien).toUpperCase();
  const porCodigo = filas.find((a) => String(a.id ?? "").toUpperCase() === cod || String(a.codigo ?? "").toUpperCase() === cod);
  if (porCodigo) return porCodigo;
  const q = normalizarNombre(quien);
  let cand = filas.filter((a) => normalizarNombre(a.descripcion) === q);
  if (!cand.length) {
    const palabras = q.split(" ").filter(Boolean);
    cand = filas.filter((a) => {
      const todas = new Set(normalizarNombre(a.descripcion).split(" "));
      return palabras.length > 0 && palabras.every((w) => todas.has(w));
    });
  }
  if (cand.length > 1) {
    const cuales = cand.slice(0, 3).map((a) => `${a.id} ${a.descripcion}`).join("; ");
    throw noHay(`Coinciden ${cand.length} artículos (${cuales}…). Usá el código.`);
  }
  if (!cand.length) throw noHay(`No encontré «${texto(quien)}» en el stock del pañol.`);
  return cand[0];
}

/* Un depósito por su clave («tractores», «bahia») o por su nombre, entero o
   un pedazo que sea de uno solo («puerto» → PUERTO - TERMINAL 4). */
export function buscarDeposito(depositos, quien) {
  const filas = depositos || [];
  const q = normalizarNombre(quien);
  let cand = filas.filter((d) => normalizarNombre(d.id) === q || normalizarNombre(d.nombre) === q);
  if (!cand.length) cand = filas.filter((d) => normalizarNombre(d.nombre).includes(q));
  if (cand.length > 1) {
    throw noHay(`Coinciden ${cand.length} depósitos (${cand.slice(0, 3).map((d) => d.nombre).join("; ")}). Usá el nombre entero.`);
  }
  if (!cand.length) {
    throw noHay(`No hay un depósito «${texto(quien)}». Los que hay: ${filas.map((d) => d.nombre).join(", ")}.`);
  }
  return cand[0];
}

/* Lo que tiene un depósito adentro. Es un texto JSON heredado de Firestore
   (`depositos.stock`), y el mismo artículo puede figurar más de una vez
   —un ingreso y su corrección, «+3» y «-3»—: lo que hay es la SUMA. También
   la usa la tabla del pañol (`shared/tablasHub`). */
export function itemsDelDeposito(dep) {
  let s = dep?.stock;
  if (typeof s === "string") {
    try {
      s = JSON.parse(s);
    } catch {
      throw new ErrorHub("#HUB", `El stock del depósito ${dep.nombre} está escrito de una forma que no se puede leer.`);
    }
  }
  return Array.isArray(s) ? s : [];
}

/* Un número de la base que puede venir como texto («6499.030») o faltar. Lo
   que falta es "", no 0: un peso sin cargar no pesa cero. */
const numeroO = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? "" : Number(v));

/* El estado de un MIC o un CRT como lo dice la pantalla. Anulado le gana a
   todo: el papel puede haber estado emitido antes. */
const ESTADO_PAPEL = { borrador: "Borrador", emitido: "Emitido", anulado: "Anulado" };
const estadoPapel = (f) => (f.anulado_en ? "Anulado" : ESTADO_PAPEL[f.estado] || f.estado || "");

/* Los papeles del chofer que el hub guarda (`chofer_documentos`), con los
   nombres con que la gente los pide. La LINTI y el curso NO están: el curso
   dejó de cargarse y la LINTI nunca se cargó, y un tipo que no existe no se
   contesta con un vacío que parece «no tiene». */
const TIPOS_DOC = {
  registro: { campo: "registro_vence", como: "el registro" },
  psicofisico: { campo: "psicofisico_vence", como: "el psicofísico" },
  peligrosas: { campo: "peligrosas_vence", como: "el curso de cargas peligrosas" },
};
const ALIAS_DOC = {
  registro: "registro", licencia: "registro", "licencia de conducir": "registro",
  "registro de conducir": "registro", lnh: "registro",
  psicofisico: "psicofisico", "apto psicofisico": "psicofisico",
  peligrosas: "peligrosas", "cargas peligrosas": "peligrosas", "curso de cargas peligrosas": "peligrosas",
};

/* Un momento argentino: el final de ese día (23:59:59, UTC-3). «El precio del
   05/10» es el último que rigió ese día, también si Carlos declaró a la tarde. */
const finDelDiaAR = (iso) => new Date(`${iso}T23:59:59-03:00`);

/* ── Dónde está cada unidad: lo mismo que el Excel de Arribos ──────────────
 *
 * Leonel Astroza (Satelital) lleva su planilla de arribos y salidas en Mis
 * planillas y quiere que la columna UBICACIÓN ACTUALIZADA se llene sola con
 * lo que pone en SATE (el interno, 740, o la patente, «KDV 538»). Ya tenía
 * eso por Excel: la herramienta de Satelital «Arribos/Salidas/Ubicaciones por
 * Excel» (POST /api/satelital/posiciones en el bot). La planilla y ese Excel
 * NO se pueden contradecir, así que acá no se decide nada sobre lugares: el
 * texto del lugar lo arma FlotaBot (GET /api/ubicaciones), con la misma fuente
 * —sólo CAR TRUCK (LYNX), pedido de Leonel del 24/09—, las mismas geocercas y
 * el mismo «Lugano → Mercado Central». Lo que vive acá es cómo se lee la
 * columna SATE, copiado de la herramienta (`interno_de_sate` y el armado de
 * cada fila en el bot):
 *
 *   · de 1 a 5 dígitos es un interno, sin ceros adelante («0740» es 740);
 *   · cualquier otra cosa es una patente, y se busca en el padrón
 *     (`patentes` del bot, sin espacios ni guiones y en mayúsculas);
 *   · si no se reconoce (una patente que no es de la flota) → NO TIENE SAT;
 *   · si se reconoce pero CAR TRUCK no la trae, o la trae con el GPS
 *     inválido, o no hay lugar para decir → SIN SEÑAL (el bot ya lo manda
 *     así, con estado «sin_senal»).
 *
 * El último punto parece un detalle y no lo es: la herramienta pone SIN SEÑAL
 * —no NO TIENE SAT— a un interno escrito con números que CAR TRUCK no trae, y
 * la planilla tiene que decir lo mismo para esa fila.
 *
 * Una excepción al «lo mismo que el Excel»: una zona con sentido (Leonel,
 * ticket 506, «SALIO DE DORREGO»). El Excel sabe si es de salidas o de
 * arribos y escribe «… AL SUR» o «… A BS AS»; la fórmula no sabe en qué hoja
 * está y dice el nombre de la zona sola. El botón y «Agregar a la planilla»
 * sí eligen por la hoja (`lugarParaHoja` de `pernocteBitren`).
 *
 * NO HAY UN CORTE POR ANTIGÜEDAD, como en el Excel (`satelital_posiciones` del
 * bot): una unidad que reportó por última vez hace un día sale con el lugar de
 * ese reporte y estado «ok»; de cuándo es lo dice el campo "hora". Si algún
 * día se quiere SIN SEÑAL por reporte viejo, va del lado del bot y lo toman
 * las dos puertas: acá no se decide. Otra cosa es que el HUB no pueda
 * actualizar: eso es #HUB a los diez minutos (VENCE_UBICACION_MS).
 */
export const SIN_SENAL = "SIN SEÑAL";
export const SIN_SAT = "NO TIENE SAT";
const CAMPOS_UBICACION = ["lugar", "hora", "estado"];
const ES_INTERNO_SATE = /^\d{1,5}$/;

/* El interno de un valor de la columna SATE, o null si no se reconoce.
   `patentes` es el mapa del bot {PATENTE: interno}. */
export function internoDeSate(sate, patentes) {
  const s = texto(sate);
  if (!s) return null;
  const crudo = ES_INTERNO_SATE.test(s) ? s : (patentes || {})[normalizarPatente(s)];
  const n = String(crudo ?? "").trim().replace(/^0+/, "");
  return n || null;
}

/* «08/10 14:05» en hora argentina, como la columna «Última señal» del Excel.
   Recibe el ISO del bot o un instante en milisegundos (`Date.now()`). */
function horaDeSenal(iso) {
  const d = enAR(iso);
  if (!d) return "";
  return `${dd(d.getUTCDate())}/${dd(d.getUTCMonth() + 1)} ${dd(d.getUTCHours())}:${dd(d.getUTCMinutes())}`;
}

/* Lugar, hora y estado de una unidad con lo que trajo FlotaBot
   ({ unidades, patentes }, ya normalizado por `services/hubDatos`). */
export function ubicacionDe(datos, sate) {
  const interno = internoDeSate(sate, datos?.patentes);
  if (!interno) return { lugar: SIN_SAT, hora: SIN_SAT, estado: SIN_SAT };
  const u = (datos?.unidades || {})[interno] || null;
  const hora = u ? horaDeSenal(u.hora) : "";
  // Con estado «ok» pero sin texto, la herramienta escribe SIN SEÑAL: acá igual.
  const lugar = u && u.estado === "ok" ? texto(u.lugar) : "";
  if (!lugar) return { lugar: SIN_SENAL, hora: hora || SIN_SENAL, estado: SIN_SENAL };
  return { lugar, hora, estado: "ok" };
}

const REGISTRO = {
  PRECIO_GASOIL: {
    firma: "HUB.PRECIO_GASOIL([fecha])",
    descripcion: "El litro de gasoil D-500 que pagamos. Con fecha, el que regía al cierre de ese día.",
    ejemplo: "=HUB.PRECIO_GASOIL()*A2",
    permisos: ["ver.ypf"],
    preparar(args) {
      cantidad(args, 0, 1, "PRECIO_GASOIL", this.firma);
      return { fecha: vacio(args[0]) ? null : fechaISO(args[0], "La fecha") };
    },
    fuentes: () => [FUENTE.gasoil],
    calcular(p, ahora, filas) {
      const momento = p.fecha ? finDelDiaAR(p.fecha) : ahora;
      const { actual } = precioDeclarado(filas, momento);
      if (!actual) throw noHay(p.fecha ? `No hay precio declarado vigente el ${p.fecha}.` : "Todavía no hay precio declarado.");
      return actual.precio;
    },
  },

  VEHICULO: {
    firma: `HUB.VEHICULO(interno; [campo])`,
    descripcion: `Un dato del padrón de la flota. El interno pelado es tractor; con F adelante, furgón (F10). Campos: ${CAMPOS_VEHICULO.join(", ")}.`,
    ejemplo: '=HUB.VEHICULO(10;"patente")',
    permisos: ["ver.flota"],
    preparar(args) {
      cantidad(args, 1, 2, "VEHICULO", this.firma);
      const u = unidadDe(args[0]);
      if (!u) throw mal(`«${texto(args[0]) || "nada"}» no es un interno.`);
      return { ...u, campo: campoDe(args[1], CAMPOS_VEHICULO, "VEHICULO") };
    },
    fuentes: (p) => (p.campo === "chofer" ? [FUENTE.flota, FUENTE.asignaciones, FUENTE.personas] : [FUENTE.flota]),
    calcular(p, ahora, flota, asignaciones, personas) {
      const fila = buscarUnidad(flota, p);
      const nombre = p.tipo === "furgon" ? `el furgón ${p.numero}` : `el tractor ${p.numero}`;
      if (!fila) throw noHay(`No está ${nombre} en el padrón.`);
      switch (p.campo) {
        case "patente": return fila.dominio || "";
        case "marca": return fila.marca || "";
        case "modelo": return fila.modelo || "";
        case "estado": return p.tipo === "furgon" ? textoDeEstado(fila.estado) : textoDeEstadoTractor(fila);
        case "tipo": return p.tipo === "furgon" ? etiquetaTipoAcoplado(fila.tipo_acoplado) : "Tractor";
        case "chofer":
          if (p.tipo === "furgon") throw noHay("Los furgones no tienen chofer asignado: preguntá por el tractor.");
          return choferesDe(p.numero, asignaciones, personas);
        default: throw mal(`Campo desconocido: ${p.campo}.`);
      }
    },
  },

  CHOFER: {
    firma: "HUB.CHOFER(legajo_dni_o_nombre; [campo])",
    descripcion: `Un dato del padrón de personal. Si el nombre coincide con varios, pedí por legajo o DNI. Campos: ${CAMPOS_CHOFER.join(", ")}.`,
    ejemplo: '=HUB.CHOFER("Pérez Juan";"tractor")',
    permisos: ["ver.gente"],
    preparar(args) {
      cantidad(args, 1, 2, "CHOFER", this.firma);
      const quien = typeof args[0] === "number" ? String(Math.trunc(args[0])) : texto(args[0]);
      if (!quien) throw mal("Falta a quién: un legajo, un DNI o un nombre.");
      return { quien, campo: campoDe(args[1], CAMPOS_CHOFER, "CHOFER") };
    },
    fuentes: (p) => (p.campo === "tractor" ? [FUENTE.personas, FUENTE.asignaciones] : [FUENTE.personas]),
    calcular(p, ahora, personas, asignaciones) {
      const per = buscarPersona(personas, p.quien);
      switch (p.campo) {
        case "nombre": return nombreDePersona(per) || "";
        case "legajo": return per.legajo ?? "";
        case "dni": return per.dni ?? "";
        case "puesto": return per.puesto && per.especializacion ? `${per.puesto} (${per.especializacion})` : (per.puesto || "");
        case "estado": return textoDeEstado(per.estado);
        case "tractor": {
          const a = (asignaciones || []).find((x) => claveDni(x.dni) === claveDni(per.dni));
          return a ? claveInterno(a.interno) : "";
        }
        default: throw mal(`Campo desconocido: ${p.campo}.`);
      }
    },
  },

  VIAJES: {
    firma: 'HUB.VIAJES(desde; hasta; [sentido]; [cliente])',
    descripcion: 'Cuántos viajes hubo entre dos fechas (las dos inclusive), sin los anulados. Cuenta como el tablero de Presidencia: por fecha de carga, o de salida si no hay, y sin los tramos cortos Río Grande ↔ Río Gallegos (no cuentan como viaje). Sentido "sur" o "norte"; para saltear uno, "".',
    ejemplo: '=HUB.VIAJES("01/10/2026";"31/10/2026";"sur")',
    permisos: ["ver.presidencia"],
    preparar(args) {
      cantidad(args, 2, 4, "VIAJES", this.firma);
      const { desde, hasta } = rangoDeFechas(args[0], args[1], null);
      const sentido = texto(args[2]).toLowerCase();
      if (sentido && sentido !== "sur" && sentido !== "norte") throw mal(`El sentido es "sur" o "norte"; llegó «${sentido}».`);
      return { desde, hasta, sentido, cliente: texto(args[3]) };
    },
    fuentes: (p) => [FUENTE.viajes(p)],
    calcular: (p, ahora, n) => n,
  },

  UREA: {
    firma: "HUB.UREA(desde; [hasta]; [interno])",
    descripcion: "Los litros de urea (AdBlue) cargados entre dos fechas, según los vales que registró el bot. Con interno, sólo los de esa unidad. Sin «hasta», sólo ese día.",
    ejemplo: '=HUB.UREA("01/10/2026";"31/10/2026";10)',
    permisos: ["ver.combustible", "ver.urea"],
    preparar(args) {
      cantidad(args, 1, 3, "UREA", this.firma);
      const { desde, hasta } = rangoDeFechas(args[0], vacio(args[1]) ? args[0] : args[1], 400);
      let unidad = null;
      if (!vacio(args[2])) {
        unidad = unidadDe(args[2]);
        if (!unidad) throw mal(`«${texto(args[2])}» no es un interno.`);
      }
      return { desde, hasta, unidad: unidad ? unidad.clave : null };
    },
    fuentes: (p) => [FUENTE.urea(p)],
    calcular(p, ahora, cargas) {
      const total = (cargas || [])
        .filter((c) => !p.unidad || (unidadDe(c.interno) || {}).clave === p.unidad)
        .reduce((s, c) => s + (Number(c.litros) || 0), 0);
      return Math.round(total * 100) / 100;
    },
  },
  /* El RTO sale de `rto_tractores` / `rto_furgones`, las mismas listas que
     las pantallas de RTO, con el veredicto que arma la base (`rto_estado`):
     la fecha que manda es la del CENT y, si el CENT no tiene nada, la que se
     declaró del título. «Sin uso?» NO aparece: es un tag de la pantalla que
     cruza la actividad, y para una cuenta vale lo que dice el organismo. */
  RTO: {
    firma: "HUB.RTO(interno; [campo])",
    descripcion: 'La revisión técnica (RTO/VTV) de la unidad, como la pantalla de RTO. Campos: "vence" (la fecha que manda: la del CENT, o la del título si el CENT no tiene nada), "estado" (Al día, Por vencer, Vencido…) y "dias" (cuántos faltan; negativo si ya venció). Con F adelante, el furgón.',
    ejemplo: '=HUB.RTO(10;"dias")',
    permisos: ["ver.documentos"],
    preparar(args) {
      cantidad(args, 1, 2, "RTO", this.firma);
      const u = unidadDe(args[0]);
      if (!u) throw mal(`«${texto(args[0]) || "nada"}» no es un interno.`);
      return { ...u, campo: campoDe(args[1], CAMPOS_RTO, "RTO") };
    },
    fuentes: (p) => [FUENTE.rto(p.tipo)],
    calcular(p, ahora, filas) {
      const fila = (filas || []).find((f) => claveInterno(f.interno) === p.numero);
      if (!fila) throw noHay(`No está ${nombreUnidad(p)} en el padrón.`);
      const estado = ETIQUETA_RTO[fila.estado] || fila.estado || "";
      if (p.campo === "estado") return estado;
      // Sin fecha, el estado ES la respuesta («Sin patente cargada», «Sin RTO
      // registrada»): un vacío se leería como «no vence».
      if (!fila.vence) throw noHay(`${mayuscula(nombreUnidad(p))}: ${estado || "sin fecha de RTO"}.`);
      return p.campo === "dias" ? Number(fila.dias) : diaAr(fila.vence);
    },
  },

  /* Las multas GUARDADAS (`multas`, la tabla de la pantalla Multas, que se
     llena cada vez que alguien consulta una patente) y cuándo se consultó
     cada una (`multas_consulta`). Las dos con la RLS de esa pantalla. */
  MULTAS: {
    firma: "HUB.MULTAS(interno_o_patente; [desde]; [hasta]; [campo])",
    descripcion: `Las multas de tránsito guardadas de una unidad (PBA, CABA y SINAI), por fecha del acta; sin fechas, todas. Campos: ${CAMPOS_MULTAS.join(", ")}. Por DNI no: esa consulta es en vivo y no se guarda.`,
    ejemplo: '=HUB.MULTAS(10;"01/01/2026";"31/12/2026";"monto")',
    permisos: ["ver.documentos", "admin.permisos"],
    preparar(args) {
      cantidad(args, 1, 4, "MULTAS", this.firma);
      const v = vehiculoDe(args[0]);
      if (!v) throw mal(`«${texto(args[0]) || "nada"}» no es un interno ni una patente.`);
      if (v.dni) {
        throw mal("Por DNI no hay multas guardadas: la consulta por persona es en vivo (pantalla Multas) y no queda en el hub. Usá el interno o la patente.");
      }
      const desde = vacio(args[1]) ? null : fechaISO(args[1], "«desde»");
      const hasta = vacio(args[2]) ? null : fechaISO(args[2], "«hasta»");
      if (desde && hasta && desde > hasta) throw mal(`«desde» (${desde}) es posterior a «hasta» (${hasta}).`);
      return { ...v, desde, hasta, campo: campoDe(args[3], CAMPOS_MULTAS, "MULTAS") };
    },
    // El padrón, sólo si hay que pasar de interno a patente.
    fuentes: (p) => (p.unidad ? [FUENTE.multas, FUENTE.flota] : [FUENTE.multas]),
    calcular(p, ahora, multas, flota) {
      let patente = p.patente;
      let quien = `la patente ${patente}`;
      if (p.unidad) {
        const fila = buscarUnidad(flota, p.unidad);
        if (!fila) throw noHay(`No está ${nombreUnidad(p.unidad)} en el padrón.`);
        patente = normalizarPatente(fila.dominio);
        if (!patente) throw noHay(`${mayuscula(nombreUnidad(p.unidad))} no tiene patente cargada en el padrón.`);
        quien = `${nombreUnidad(p.unidad)} (${patente})`;
      }
      /* NUNCA CONSULTADA NO ES «SIN MULTAS». Un 0 de una patente que nadie
         miró afirma algo que nadie verificó. */
      const consultada = (multas?.consultas || []).some((c) => normalizarPatente(c.dominio) === patente);
      if (!consultada) throw noHay(`Las multas de ${quien} no se consultaron nunca: un 0 sería decir que no tiene sin haber mirado.`);
      const dia = (m) => String(m.ocurrida_en || "").slice(0, 10);
      const enRango = (m) => (!p.desde && !p.hasta) || (dia(m) && (!p.desde || dia(m) >= p.desde) && (!p.hasta || dia(m) <= p.hasta));
      const filas = (multas?.actas || []).filter((m) => normalizarPatente(m.dominio) === patente && enRango(m));
      const impagas = filas.filter((m) => !m.pagada);
      const suma = (fs) => Math.round(fs.reduce((s, m) => s + (Number(m.monto) || 0), 0) * 100) / 100;
      switch (p.campo) {
        case "cantidad": return filas.length;
        case "monto": return suma(filas);
        case "impagas": return impagas.length;
        case "monto_impago": return suma(impagas);
        default: throw mal(`Campo desconocido: ${p.campo}.`);
      }
    },
  },

  /* Los vencimientos de `chofer_documentos`, la tabla de la pantalla
     Documentos (la planilla que mantiene Marcos, sincronizada de noche). */
  DOC_VENCE: {
    firma: "HUB.DOC_VENCE(dni; [tipo])",
    descripcion: 'Cuándo le vence un papel a un chofer, según Documentos. Tipos: "registro" (o "licencia", el que va si no se dice), "psicofisico" y "peligrosas" (el curso de cargas peligrosas). La LINTI y el curso no se cargan en el hub.',
    ejemplo: '=HUB.DOC_VENCE(30111222;"registro")-HOY()',
    permisos: ["ver.documentos"],
    preparar(args) {
      cantidad(args, 1, 2, "DOC_VENCE", this.firma);
      const dni = claveDni(typeof args[0] === "number" ? String(Math.trunc(args[0])) : args[0]);
      if (!dni) throw mal(`«${texto(args[0]) || "nada"}» no es un DNI.`);
      const t = normalizarNombre(args[1]) || "registro";
      const tipo = ALIAS_DOC[t];
      if (!tipo) throw mal(`El hub no guarda «${texto(args[1])}». Los tipos que hay: registro (licencia), psicofisico, peligrosas.`);
      return { dni, tipo };
    },
    fuentes: () => [FUENTE.documentos],
    calcular(p, ahora, filas) {
      const fila = (filas || []).find((f) => claveDni(f.dni) === p.dni);
      if (!fila) throw noHay(`El DNI ${p.dni} no está en la planilla de Documentos.`);
      const { campo, como } = TIPOS_DOC[p.tipo];
      const quien = fila.nombre || `El DNI ${p.dni}`;
      if (!fila[campo]) {
        if (p.tipo === "peligrosas" && !fila.requiere_peligrosas) throw noHay(`${quien} no necesita el curso de cargas peligrosas (no está marcado (P)).`);
        // Sin fecha NO es al día: el papel existe y nadie sabe hasta cuándo vale.
        throw noHay(`${quien}: ${como} no tiene fecha cargada.`);
      }
      return diaAr(fila[campo]);
    },
  },

  /* Los MIC de la pantalla de aduana (`mic`, RLS de `ver.aduana`). El CRT se
     nombra por su número, que es lo que se lee en el papel: por eso «crt»
     trae también la lista de CRT. */
  MIC: {
    firma: "HUB.MIC(numero; [campo])",
    descripcion: `Un dato de un MIC/DTA, por su número entero o sólo el correlativo. Campos: ${CAMPOS_MIC.join(", ")}.`,
    ejemplo: '=HUB.MIC("AR.000.000128";"placa")',
    permisos: ["ver.aduana"],
    preparar(args) {
      cantidad(args, 1, 2, "MIC", this.firma);
      if (vacio(args[0])) throw mal("Falta el número del MIC.");
      return { numero: args[0], campo: campoDe(args[1], CAMPOS_MIC, "MIC") };
    },
    /* «hoja» también trae los CRT: sin camiones congelados en el MIC, la M
       sale del CRT si la declaró después, o de los emitidos hasta ahora. */
    fuentes: (p) => (p.campo === "crt" || p.campo === "hoja" ? [FUENTE.mic, FUENTE.crt] : [FUENTE.mic]),
    calcular(p, ahora, mics, crts) {
      const m = buscarNumero(mics, p.numero, "MIC");
      switch (p.campo) {
        case "estado": return estadoPapel(m);
        case "fecha": return diaAr(m.fecha_emision);
        case "crt": {
          const c = (crts || []).find((x) => String(x.id) === String(m.crt_id));
          return c ? c.numero || "" : "";
        }
        case "hoja": {
          if (!m.hoja) return "";
          const c = (crts || []).find((x) => String(x.id) === String(m.crt_id)) || { id: m.crt_id };
          const total = hojasDe(m, c, mics);
          return total ? `${m.hoja} de ${total}` : "";
        }
        case "placa": return m.placa || "";
        case "acoplado": return m.acoplado_placa || "";
        case "chofer": return m.motorista || "";
        case "peso": return numeroO(m.peso_bruto);
        case "bultos": return numeroO(m.bultos);
        case "partida": return m.aduana_partida || "";
        case "destino": return m.aduana_destino || "";
        default: throw mal(`Campo desconocido: ${p.campo}.`);
      }
    },
  },

  CRT: {
    firma: "HUB.CRT(numero; [campo])",
    descripcion: `Un dato de una carta de porte (CRT), por su número entero o sólo el correlativo. Campos: ${CAMPOS_CRT.join(", ")}.`,
    ejemplo: '=HUB.CRT("AR.TCC.000001";"peso")',
    permisos: ["ver.aduana"],
    preparar(args) {
      cantidad(args, 1, 2, "CRT", this.firma);
      if (vacio(args[0])) throw mal("Falta el número del CRT.");
      return { numero: args[0], campo: campoDe(args[1], CAMPOS_CRT, "CRT") };
    },
    fuentes: () => [FUENTE.crt],
    calcular(p, ahora, crts) {
      const c = buscarNumero(crts, p.numero, "CRT");
      // Las partes como las dibuja el CRT: la razón social de la casilla.
      const parte = (rol) => (comoParte(c[rol]) || {}).razon_social || "";
      switch (p.campo) {
        case "estado": return estadoPapel(c);
        case "emitido": return diaAr(c.emitido_en);
        case "carga": return diaAr(c.carga_fecha);
        case "origen": return c.carga_lugar || "";
        case "entrega": return c.entrega_lugar || "";
        case "remitente": return parte("remitente");
        case "destinatario": return parte("destinatario");
        case "mercaderia": return c.mercaderia || "";
        case "peso": return numeroO(c.peso_bruto);
        case "bultos": return numeroO(c.bultos);
        case "valor": return numeroO(c.valor_monto);
        case "moneda": return c.valor_moneda || "";
        case "camiones": return numeroO(c.camiones);
        default: throw mal(`Campo desconocido: ${p.campo}.`);
      }
    },
  },

  /* El pañol: `stock` es el catálogo con la cantidad total (la pantalla
     Stock) y `depositos` lo que hay en cada uno (la pantalla Depósitos). */
  STOCK: {
    firma: "HUB.STOCK(artículo_o_código; [depósito])",
    descripcion: "Cuántas unidades hay en el pañol de un artículo, por su código o su descripción. Sin depósito, el total de la pantalla Stock; con depósito (\"tractores\", \"Bahía Blanca\"…), lo que hay en ése.",
    ejemplo: '=HUB.STOCK("MT000001";"tractores")',
    permisos: ["ver.panol"],
    preparar(args) {
      cantidad(args, 1, 2, "STOCK", this.firma);
      const art = texto(args[0]);
      if (!art) throw mal("Falta el artículo: su código o su descripción.");
      return { articulo: art, deposito: texto(args[1]) };
    },
    fuentes: (p) => (p.deposito ? [FUENTE.stock, FUENTE.depositos] : [FUENTE.stock]),
    calcular(p, ahora, stock, depositos) {
      const art = buscarArticulo(stock, p.articulo);
      if (!p.deposito) {
        const n = numeroO(art.cantidad);
        if (n === "") throw noHay(`${art.id} ${art.descripcion} no tiene cantidad cargada.`);
        return n;
      }
      const dep = buscarDeposito(depositos, p.deposito);
      // Que no figure en el depósito es un cero de verdad: la lista del
      // depósito es todo lo que tiene.
      const total = itemsDelDeposito(dep)
        .filter((it) => String(it?.id ?? "").toUpperCase() === String(art.id).toUpperCase())
        .reduce((s, it) => s + (Number(it.cantidad) || 0), 0);
      return Math.round(total * 1000) / 1000;
    },
  },

  /* Ver «Dónde está cada unidad» más arriba. La llave es la de las pantallas
     de Satelital y Tráfico que muestran posiciones (En ruta, Recorrido…) y la
     misma que pide el bot en /api/satelital/posiciones y /api/ubicaciones. */
  UBICACION: {
    firma: "HUB.UBICACION(interno_o_patente; [campo])",
    descripcion: 'Dónde está ahora una unidad según el satelital CAR TRUCK (LYNX): el mismo texto que la herramienta de Arribos/Salidas por Excel de Satelital. Va el interno (740, "0740") o la patente ("KDV 538", "AF-887-ZV"). Si la unidad no se reconoce dice «NO TIENE SAT»; si CAR TRUCK no la trae o no tiene una posición válida, «SIN SEÑAL». No corta por antigüedad (como el Excel): de cuándo es el último reporte lo dice "hora". Campos: "lugar" (el que va si no se dice), "hora" (el último reporte, dd/mm hh:mm) y "estado" (ok, SIN SEÑAL o NO TIENE SAT). Con la planilla abierta se actualiza sola cada uno o dos minutos; si pasan diez sin poder actualizarla, #HUB en vez de un lugar viejo.',
    ejemplo: "=HUB.UBICACION(740)",
    permisos: ["ver.trafico", "cargar.trafico", "cargar.satelital"],
    preparar(args) {
      cantidad(args, 1, 2, "UBICACION", this.firma);
      return { sate: texto(args[0]), campo: campoDe(args[1], CAMPOS_UBICACION, "UBICACION") };
    },
    // Una fila sin nada en SATE queda vacía, como en el Excel, y no pide nada.
    fuentes: (p) => (p.sate ? [FUENTE.ubicaciones] : []),
    calcular(p, ahora, datos) {
      if (!p.sate) return "";
      return ubicacionDe(datos, p.sate)[p.campo];
    },
  },
};

/* Para la ayuda de la pantalla: firma, para qué sirve, un ejemplo y el
   permiso que pide. */
export const FUNCIONES_HUB = Object.entries(REGISTRO).map(([nombre, f]) => ({
  nombre: `HUB.${nombre}`, firma: f.firma, descripcion: f.descripcion, ejemplo: f.ejemplo, permisos: f.permisos,
}));

/* ── El caché y el `hub` que recibe el motor ───────────────────────────────
 *
 *   crearHub({ datos, alLlegar, ahora, programar })
 *
 * `datos` es la capa que lee (en la pantalla, `services/hubDatos`; en los
 * tests, un objeto de mentira). `alLlegar` se llama cuando llegó algo y vale
 * la pena recalcular: varios datos que llegan juntos son UN aviso. `ahora` y
 * `programar` se pueden cambiar para probar sin reloj. */
export function crearHub({
  datos,
  alLlegar = () => {},
  ahora = () => Date.now(),
  programar = (fn) => setTimeout(fn, 0),
  ttl = TTL_MS,
  ttlError = TTL_ERROR_MS,
} = {}) {
  const cache = new Map(); // clave → { datos, hay, error, fallo, t, leido, promesa }
  let usado = false;
  let cerrado = false;
  let perdido = false; // llegó algo mientras estaba cerrado
  let avisoPendiente = false;

  const avisar = () => {
    if (cerrado) { perdido = true; return; }
    if (avisoPendiente) return;
    avisoPendiente = true;
    programar(() => {
      avisoPendiente = false;
      if (cerrado) perdido = true;
      else alLlegar();
    });
  };

  function lanzar(fuente, e) {
    const entrada = e || { hay: false, datos: null, error: null, fallo: null, t: 0, leido: 0, promesa: null };
    cache.set(fuente.clave, entrada);
    let p;
    try {
      p = Promise.resolve(fuente.traer(datos));
    } catch (err) {
      p = Promise.reject(err);
    }
    entrada.promesa = p.then(
      (d) => {
        entrada.datos = d; entrada.hay = true; entrada.error = null; entrada.fallo = null;
        entrada.t = ahora(); entrada.leido = entrada.t;
      },
      (err) => {
        // Con un dato viejo en la mano se sigue mostrando ése (salvo que la
        // fuente tenga `vence`: ver `consultar`) y se reintenta en
        // TTL_ERROR_MS; sin dato, la celda dice qué no se pudo leer.
        const porque = err && err.message ? err.message : String(err);
        if (entrada.hay) {
          entrada.fallo = porque;
          entrada.t = ahora() - ttl + ttlError;
        } else {
          entrada.error = `No se pudo leer ${fuente.que}: ${porque}`;
          entrada.t = ahora();
        }
      },
    ).then(() => {
      entrada.promesa = null;
      avisar();
    });
    return entrada;
  }

  /* Lo que hay de una fuente AHORA, y si hace falta, sale a buscarla. */
  function consultar(fuente) {
    const e = cache.get(fuente.clave);
    if (!e) {
      lanzar(fuente);
      return { estado: "cargando" };
    }
    const edad = ahora() - e.t;
    if (e.hay) {
      if (edad > ttl && !e.promesa) lanzar(fuente, e);
      // Vencida de verdad: lo viejo ya no se muestra. Si lo último fue un
      // error, se dice; si no (nadie preguntó en un rato), «…» hasta que llega.
      if (fuente.vence && ahora() - e.leido > fuente.vence) {
        if (!e.fallo) return { estado: "cargando" };
        return { estado: "error", motivo: `No se pudo actualizar ${fuente.que} desde el ${horaDeSenal(e.leido)}: ${e.fallo}` };
      }
      return { estado: "ok", datos: e.datos };
    }
    if (e.promesa) return { estado: "cargando" };
    if (e.error && edad <= ttlError) return { estado: "error", motivo: e.error };
    lanzar(fuente, e);
    return { estado: "cargando" };
  }

  function leer(nombre, args) {
    usado = true;
    const def = REGISTRO[String(nombre).toUpperCase()];
    if (!def) {
      return { error: "#NOMBRE", motivo: `No hay una función HUB.${nombre}. Las que hay: ${FUNCIONES_HUB.map((f) => f.nombre).join(", ")}.` };
    }
    try {
      const params = def.preparar(args || []);
      // El permiso primero: sin él no se sale a buscar algo que no se va a
      // mostrar.
      const per = consultar(FUENTE.permisos);
      if (per.estado === "cargando") return { cargando: true };
      if (per.estado === "error") return { error: "#HUB", motivo: per.motivo };
      const mios = Array.isArray(per.datos) ? per.datos : [];
      if (def.permisos && !def.permisos.some((x) => mios.includes(x))) {
        return { error: "#HUB", motivo: `No tenés acceso a HUB.${String(nombre).toUpperCase()}.` };
      }
      const estados = def.fuentes(params).map(consultar);
      const fallo = estados.find((s) => s.estado === "error");
      if (fallo) return { error: "#HUB", motivo: fallo.motivo };
      if (estados.some((s) => s.estado !== "ok")) return { cargando: true };
      const valor = def.calcular(params, new Date(ahora()), ...estados.map((s) => s.datos));
      return { valor };
    } catch (e) {
      if (e instanceof ErrorHub) return { error: e.codigo, motivo: e.motivo };
      return { error: "#HUB", motivo: e && e.message ? e.message : String(e) };
    }
  }

  return {
    leer,
    /* ¿Alguna fórmula preguntó algo? Si no, no hace falta refrescar nada. */
    usado: () => usado,
    /* Los pedidos en viaje (para los tests, y para esperar antes de exportar). */
    enViaje: () => [...cache.values()].map((e) => e.promesa).filter(Boolean),
    /* La pantalla se fue: lo que llegue después no avisa a nadie. Y volvió
       (el StrictMode de React desmonta y monta los efectos una vez de prueba):
       si llegó algo en el medio, se avisa ahora. */
    cerrar: () => { cerrado = true; },
    abrir: () => {
      cerrado = false;
      if (perdido) { perdido = false; avisar(); }
    },
  };
}
